import { useEffect, useState } from 'react';

import { api } from '../../../api';
import type { FeatureContext } from '../../types';
import './controller.css';

export function ControllerFeature({ isAuthenticated }: FeatureContext) {
  const [order, setOrder] = useState<string[]>([]);
  const [currentName, setCurrentName] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [state, setState] = useState('not_started');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setError('Sign in to manage Sorting Hat assignments.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const state = await api.getSortingHatState();

        if (cancelled) {
          return;
        }

        setOrder(state.order);
        const name = state.order[state.current_index] ?? null;
        setCurrentName(name);
        setCurrentIndex(state.current_index);
        setState(state.state);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load assignments.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  async function handleRefresh() {
    setSaving(true);
    setError(null);
    try {
      const state = await api.getSortingHatState();
      setOrder(state.order);
      const name = state.order[state.current_index] ?? null;
      setCurrentName(name);
      setCurrentIndex(state.current_index);
      setState(state.state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to refresh state.');
    } finally {
      setSaving(false);
    }
  }

  async function handleStartSorting() {
    setSaving(true);
    setError(null);

    try {
      const state = await api.startSortingHat();
      setOrder(state.order);
      const name = state.order[state.current_index] ?? null;
      setCurrentName(name);
      setCurrentIndex(state.current_index);
      setState(state.state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start sorting.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestartSorting() {
    setSaving(true);
    setError(null);

    try {
      const state = await api.restartSortingHat();
      setOrder(state.order);
      const name = state.order[state.current_index] ?? null;
      setCurrentName(name);
      setCurrentIndex(state.current_index);
      setState(state.state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to restart sorting.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="page-section riya-controller" aria-live="polite">
      <div className="riya-controller__card">
        <div className="riya-controller__header">
          <div>
            <h2>Sorting Hat Controller</h2>
            <p>Generate a random sorting order and monitor the current guest.</p>
          </div>
          <div className="riya-controller__status">
            <span className="riya-controller__label">Selected</span>
            <strong>{currentName ?? 'None'}</strong>
          </div>
        </div>

        <div className="riya-controller__actions">
          <button
            type="button"
            className="riya-controller__primary"
            onClick={handleStartSorting}
            disabled={saving}
          >
            Randomize Order
          </button>
          <button
            type="button"
            className="riya-controller__secondary"
            onClick={handleRestartSorting}
            disabled={saving || order.length === 0}
          >
            Restart Sorting
          </button>
          <button
            type="button"
            className="riya-controller__secondary"
            onClick={handleRefresh}
            disabled={saving}
          >
            Refresh
          </button>
          <div className="riya-controller__meta">
            <span>Position</span>
            <strong>{order.length ? `${currentIndex + 1} / ${order.length}` : '—'}</strong>
          </div>
          {state !== 'not_started' && (
            <span className="riya-controller__badge">Sorting live</span>
          )}
        </div>

        {loading && <p className="riya-controller__note">Loading assignments...</p>}
        {error && (
          <p className="riya-controller__error" role="alert">
            {error}
          </p>
        )}

        {!loading && order.length > 0 && (
          <ol className="riya-controller__order">
            {order.map((name, index) => {
              const isSelected = index === currentIndex;
              return (
                <li
                  key={name}
                  className={`riya-controller__order-item${isSelected ? ' riya-controller__order-item--active' : ''}`}
                >
                  <span>{index + 1}. {name}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
